/**
 * 键值存储抽象
 *
 * 为什么不让业务代码直接调 localStorage：
 * 1. 弱网/隐私模式下 localStorage 可能直接抛异常，需要一个安全壳；
 * 2. 测试里要能在 node 环境跑（没有 localStorage），注入内存实现即可；
 * 3. 将来迁到小游戏平台时，storage API 完全不同 —— 换一个实现就行。
 */

export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

/** 内存实现：测试与"隐私模式降级"都用它 */
export class MemoryStore implements KeyValueStore {
  private readonly map = new Map<string, string>();

  get(key: string): string | null {
    return this.map.get(key) ?? null;
  }

  set(key: string, value: string): void {
    this.map.set(key, value);
  }

  remove(key: string): void {
    this.map.delete(key);
  }
}

/** localStorage 实现；不可用时静默降级到内存（不因为存储失败就白屏） */
export class BrowserStore implements KeyValueStore {
  private readonly fallback = new MemoryStore();
  private readonly available: boolean;

  constructor() {
    this.available = probe();
  }

  get(key: string): string | null {
    if (!this.available) return this.fallback.get(key);
    try {
      return window.localStorage.getItem(key);
    } catch {
      return this.fallback.get(key);
    }
  }

  set(key: string, value: string): void {
    if (!this.available) {
      this.fallback.set(key, value);
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch {
      this.fallback.set(key, value);
    }
  }

  remove(key: string): void {
    if (!this.available) {
      this.fallback.remove(key);
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch {
      this.fallback.remove(key);
    }
  }
}

function probe(): boolean {
  try {
    const probeKey = "__chuandeng_probe__";
    window.localStorage.setItem(probeKey, "1");
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

export function readJson<T>(store: KeyValueStore, key: string, fallback: T): T {
  const raw = store.get(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function writeJson(store: KeyValueStore, key: string, value: unknown): void {
  try {
    store.set(key, JSON.stringify(value));
  } catch {
    // 存储不可用就算了：本地进度丢了不影响继续玩
  }
}
